import { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { usePortalContainer } from "@/widgets/core/PortalContainer"

/**
 * TRexLoader — optimistic flight-progress loader with a no-chrome T-Rex
 * easter-egg. Replaces <LoadingOverlay> (overlay variant) and the Loader2
 * spinner in FlightSection (inline variant).
 *
 * • Plane runs on its OWN smooth, continuous loop (decoupled from `value`).
 * • The progress bar reflects the REAL `value` (0–100). Pass value={-1} for
 *   an indeterminate bar (bar stays empty, T-Rex loops without a dodge event).
 * • A T-Rex sprints across the bar each plane loop (~70% of the loop) and the
 *   plane hops to dodge it. (trexTrigger="atValue" fires it once when value ≥ 70.)
 * • Accent = var(--primary). Line-art = currentColor via text-foreground wrapper.
 * • Respects prefers-reduced-motion (static bar, no SVG motion).
 *
 * Required keyframes: trex-cloud-drift, trex-step, trex-dust (in src/index.css).
 */

export interface TRexLoaderProps {
  /** Real progress 0–100. Pass -1 for indeterminate (bar stays at 0). */
  value: number
  /** When the T-Rex appears. "loop" = every plane loop; "atValue" = once when value ≥ 70. */
  trexTrigger?: "loop" | "atValue"
  /** Duration of one full plane loop in ms. */
  planeCycleMs?: number
  /**
   * "overlay" (default) — renders a fixed fullscreen backdrop via portal.
   * "inline"            — renders only the widget, no backdrop, no portal.
   */
  variant?: "overlay" | "inline"
  /** Optional status text shown under the bar. */
  label?: string
  className?: string
}

const W = 320
const H = 120
const GROUND_Y = H - 1
const DEP_X = 40
const ARR_X = W - 40
const CRUISE_X = W * 0.47
const MAX_LIFT = 40
const GROUND_REST = 13
const CLIMB = 0.16
const DESCEND = 0.88
const TREX_START = 0.54
const TREX_END = 0.72
const TREX_SCALE = 0.3
const TREX_H = 124 * TREX_SCALE
const ATVALUE_RUN_MS = 2200

const PLANE_D =
  "M52.452 43L37.438 26.317A7 7 0 0 0 32.235 24h-5.882a.618.618 0 0 0-.553.894L34.852 43H18.067l-3.513-4.471A4 4 0 0 0 11.409 37H6.353a.705.705 0 0 0-.664.942l4.63 12.964q.021.055.073.08a.2.2 0 0 0 .08.061l8.207 3.283A37.3 37.3 0 0 0 32.54 57h28.626c3.082 0 6.127-.68 8.916-1.992l1.555-.732a4.726 4.726 0 0 0 0-8.553l-1.555-.731A20.95 20.95 0 0 0 61.167 43z"
const TREX_D =
  "M51.8242 0C51.7961 1.89193 51.737 3.85811 51.7402 5.74316L57.5498 5.78809C57.6347 18.2838 57.398 30.9868 57.5811 43.4414C58.8977 43.4297 62.1382 43.4839 63.3037 43.3291L63.3848 49.1865C65.9817 49.1671 69.1123 49.2278 71.6641 49.1074L71.6816 54.7793C74.202 54.7631 78.2132 54.8638 80.6133 54.6816C80.6212 56.5323 80.6493 58.4166 80.6357 60.2637L86.4961 60.2197V66.4297C89.8723 66.4331 94.3278 66.3092 97.6104 66.4756L97.6084 60.2412L103.798 60.248L103.736 54.7285L109.345 54.7305C109.314 51.2533 109.409 46.8174 109.235 43.4258L115 43.418L114.999 78.1475C113.201 78.16 111.131 78.1256 109.355 78.2305L109.43 83.4873L103.858 83.5322C103.872 85.5669 103.925 87.7296 103.854 89.7529C101.784 89.5785 99.7333 89.7012 97.6826 89.623C97.6711 91.5191 97.6996 93.5779 97.6289 95.46C95.3973 95.45 93.052 95.4029 90.8301 95.4648C89.144 96.8637 88.0556 98.0874 86.7471 99.8506C86.3837 102.703 86.5217 105.96 86.4932 108.858C86.4439 113.881 86.5667 118.966 86.4824 123.984L74.4795 123.979C74.4759 122.4 74.5445 119.842 74.3965 118.343C76.3105 118.357 78.7009 118.314 80.5781 118.398C80.5856 116.427 80.6132 114.424 80.5947 112.456C78.6085 112.446 76.352 112.487 74.3877 112.404C74.4888 110.357 74.4629 108.641 74.3809 106.588L68.8086 106.564C68.8313 104.616 68.8397 102.667 68.833 100.718L63.2676 100.697C63.3265 102.713 63.3863 104.581 63.3145 106.603L58.085 106.591L57.6279 106.731C57.4821 107.73 57.5636 111.39 57.5654 112.552C57.5798 116.364 57.5656 120.177 57.5234 123.989C53.5767 123.984 49.5043 124.029 45.5684 123.966C45.5805 122.138 45.6326 120.15 45.5605 118.336L51.6543 118.349L51.6641 95.4531L45.582 95.4111L45.6006 89.6309C43.7525 89.709 41.9606 89.6557 40.1143 89.6152L40.1221 80.6807L34.3926 80.6289L34.4053 60.4082C32.447 60.3746 30.5075 60.3275 28.5488 60.3896L28.585 66.5C26.5061 66.5085 24.3695 66.5441 22.2959 66.5117L22.3643 54.7246C26.359 54.7627 30.354 54.7615 34.3486 54.7217C34.4086 50.9535 34.4298 47.1844 34.4111 43.416C26.7175 43.3776 19.0235 43.3886 11.3301 43.4473C11.3226 41.5132 11.2853 39.5014 11.3252 37.5742C17.0725 37.6155 22.8201 37.6278 28.5674 37.6104C28.5868 35.6696 28.5958 33.7279 28.5938 31.7871C19.445 31.7252 8.95827 32.0707 0.00390625 31.7461L0 5.77832L5.7207 5.78027L5.62207 0.0166016L51.8242 0ZM32.3398 9.69043C32.3141 11.6967 32.3477 14.1049 32.2383 16.0771C33.7693 16.0831 36.8343 16.1792 38.2256 15.9395C38.1045 14.4923 38.0493 11.2027 38.2051 9.7373L32.3398 9.69043Z"
const TOWER_D =
  "M5.91657 2.16667L5.4999 5.5V11.8333M2.58323 2.16667L2.9999 5.5V11.8333M3.41657 0.5H5.08323M4.2499 0.5V2.16667M7.63448 2.16667H0.865318C0.808764 2.16668 0.752986 2.17982 0.702374 2.20506C0.651761 2.23029 0.607696 2.26693 0.573648 2.31208C0.539599 2.35724 0.516497 2.40968 0.506158 2.46528C0.495819 2.52088 0.498527 2.57812 0.514068 2.6325L0.987818 4.29083C1.15448 4.87292 1.2374 5.16375 1.4599 5.33208C1.68323 5.5 1.98532 5.5 2.59032 5.5H5.90948C6.51448 5.5 6.81698 5.5 7.0399 5.33208C7.2624 5.16375 7.34573 4.87292 7.51198 4.29125L7.98573 2.63292C8.00135 2.57851 8.00412 2.52123 7.99382 2.46557C7.98352 2.40992 7.96043 2.35742 7.92637 2.31221C7.89231 2.267 7.84822 2.23032 7.79756 2.20507C7.74691 2.17981 7.69108 2.16666 7.63448 2.16667Z"

const STROKE = 2

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false)
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)")
    const on = () => setReduced(mq.matches)
    on()
    mq.addEventListener("change", on)
    return () => mq.removeEventListener("change", on)
  }, [])
  return reduced
}

function useClock(active: boolean) {
  const [t, setT] = useState(0)
  useEffect(() => {
    if (!active) return
    let raf = 0
    const loop = (now: number) => {
      setT(now)
      raf = requestAnimationFrame(loop)
    }
    raf = requestAnimationFrame(loop)
    return () => cancelAnimationFrame(raf)
  }, [active])
  return t
}

function Cloud({
  x,
  y,
  s = 1,
  op = 0.3,
}: {
  x: number
  y: number
  s?: number
  op?: number
}) {
  return (
    <g
      transform={`translate(${x} ${y}) scale(${s})`}
      opacity={op}
      stroke="currentColor"
      strokeWidth={STROKE}
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      vectorEffect="non-scaling-stroke"
    >
      <path d="M-18 6 C -24 6 -24 -2 -17 -2 C -16 -10 -4 -10 -2 -3 C 4 -8 14 -3 12 3 C 18 3 18 6 12 6 Z" />
    </g>
  )
}

function Tower({ x, opacity }: { x: number; opacity: number }) {
  const h = 34
  const s = h / 12
  const w = 9 * s
  if (opacity <= 0.01) return null
  return (
    <g transform={`translate(${x - w / 2} ${GROUND_Y - h})`} opacity={opacity}>
      <g transform={`scale(${s})`}>
        <path
          d={TOWER_D}
          fill="currentColor"
          fillOpacity={0.16}
          stroke="currentColor"
          strokeWidth={STROKE}
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
      </g>
    </g>
  )
}

function TRexWidget({
  value,
  trexTrigger = "loop",
  planeCycleMs = 9000,
  label,
  className,
}: Omit<TRexLoaderProps, "variant">) {
  const reduced = usePrefersReducedMotion()
  const t = useClock(!reduced)
  const v = value < 0 ? 0 : Math.max(0, Math.min(100, value))
  const indeterminate = value < 0

  // ── plane loop (decoupled from value) ──
  const q = reduced ? 0.45 : (t % planeCycleMs) / planeCycleMs
  const phase: "take-off" | "cruise" | "landing" =
    q < CLIMB ? "take-off" : q < DESCEND ? "cruise" : "landing"

  const toRaw = phase === "take-off" ? q / CLIMB : 0
  const toE = Math.pow(toRaw, 3)
  const landRaw =
    phase === "landing" ? (q - DESCEND) / (1 - DESCEND) : 0
  const landE = 1 - Math.pow(1 - landRaw, 2)

  let x = CRUISE_X
  if (phase === "take-off") x = DEP_X + toE * (CRUISE_X - DEP_X)
  else if (phase === "landing") x = CRUISE_X + landE * (ARR_X - CRUISE_X)

  let lift = MAX_LIFT
  if (phase === "take-off")
    lift = GROUND_REST + toE * (MAX_LIFT - GROUND_REST)
  else if (phase === "landing")
    lift = GROUND_REST + (1 - landE) * (MAX_LIFT - GROUND_REST)
  const bob = phase === "cruise" ? Math.sin(q * 60) * 1.4 : 0

  // ── T-Rex ──
  const atValueStart = useRef<number | null>(null)
  const prevV = useRef(v)
  useEffect(() => {
    if (trexTrigger === "atValue" && prevV.current < 70 && v >= 70) {
      atValueStart.current = performance.now()
    }
    prevV.current = v
  }, [v, trexTrigger])

  let trexActive = false
  let trexU = 0
  if (trexTrigger === "loop") {
    trexActive = q >= TREX_START && q <= TREX_END
    trexU = trexActive ? (q - TREX_START) / (TREX_END - TREX_START) : 0
  } else if (atValueStart.current != null) {
    const el = t - atValueStart.current
    if (el >= 0 && el <= ATVALUE_RUN_MS) {
      trexActive = true
      trexU = el / ATVALUE_RUN_MS
    }
  }
  const trexX = W + 24 - trexU * (W + 48)

  let dodge = 0
  if (trexActive) {
    const d = Math.abs(trexX - x)
    if (d < 70) dodge = (1 - d / 70) * 30
  }

  const y = GROUND_Y - lift + bob - dodge
  const pitch =
    (phase === "take-off" ? -13 : phase === "landing" ? 12 : 0) -
    dodge * 0.5

  const depOpacity = Math.max(0, Math.min(1, 1 - (q - CLIMB) / 0.1))
  const arrOpacity = Math.max(
    0,
    Math.min(1, 1 - (DESCEND - q) / 0.1)
  )
  const driftDur = Math.max(6, planeCycleMs / 1200)

  return (
    <div
      className={`text-foreground ${className ?? ""}`}
      style={{
        width: "100%",
        maxWidth: 360,
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
      role="progressbar"
      aria-valuenow={indeterminate ? undefined : Math.round(v)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label ?? "Searching flights"}
    >
      {!reduced && (
        <svg
          width="100%"
          viewBox={`0 0 ${W} ${H}`}
          fill="none"
          style={{ overflow: "hidden", display: "block" }}
        >
          {/* clouds — continuous drift loop */}
          <g
            style={{
              animation: `trex-cloud-drift ${driftDur}s linear infinite`,
            }}
          >
            {[0, 1].map((k) => (
              <g key={k} transform={`translate(${k * W} 0)`}>
                <Cloud x={54} y={26} s={0.95} op={0.3} />
                <Cloud x={184} y={44} s={0.7} op={0.24} />
                <Cloud x={272} y={22} s={1.0} op={0.28} />
              </g>
            ))}
          </g>

          {/* airport towers */}
          <Tower x={DEP_X} opacity={depOpacity} />
          <Tower x={ARR_X} opacity={arrOpacity} />

          {/* T-Rex */}
          {trexActive && (
            <g
              transform={`translate(${trexX} ${GROUND_Y - TREX_H / 2})`}
            >
              {/* dust puff */}
              <g
                transform={`translate(${TREX_H * 0.45} ${TREX_H * 0.46})`}
                style={{
                  animation: "trex-dust 0.5s linear infinite",
                }}
              >
                <circle r="2.6" fill="currentColor" opacity={0.16} />
                <circle
                  cx="5"
                  cy="2"
                  r="1.8"
                  fill="currentColor"
                  opacity={0.1}
                />
              </g>
              {/* body */}
              <g
                style={{
                  animation:
                    "trex-step 0.24s steps(2) infinite",
                  transformBox: "fill-box",
                  transformOrigin: "center bottom",
                }}
              >
                <g
                  transform={`scale(${TREX_SCALE}) translate(-57.5 -62)`}
                >
                  <path
                    d={TREX_D}
                    fill="currentColor"
                    fillOpacity={0.25}
                    stroke="currentColor"
                    strokeWidth={STROKE}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    vectorEffect="non-scaling-stroke"
                  />
                </g>
              </g>
            </g>
          )}

          {/* plane */}
          <g
            style={{
              transform: `translate(${x}px, ${y}px) rotate(${pitch}deg)`,
            }}
          >
            <g transform="scale(0.6) translate(-38 -40)">
              {/* opaque backing so clouds never show through */}
              <path
                d={PLANE_D}
                fill="var(--background)"
                stroke="var(--background)"
                strokeWidth="4"
                strokeLinejoin="round"
              />
              <path
                d={PLANE_D}
                fill="var(--primary)"
                fillOpacity={0.22}
                stroke="var(--primary)"
                strokeWidth={STROKE}
                strokeLinecap="round"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
              />
            </g>
          </g>
        </svg>
      )}

      {/* progress bar */}
      <div
        style={{
          position: "relative",
          width: "100%",
          height: 10,
          borderRadius: 10,
          overflow: "hidden",
          background:
            "color-mix(in srgb, var(--primary) 20%, transparent)",
        }}
      >
        <div
          style={
            indeterminate
              ? {
                  position: "absolute",
                  top: 0,
                  bottom: 0,
                  background: "var(--primary)",
                  borderRadius: 10,
                  animation: "trex-bar-indeterminate 1.6s ease-in-out infinite",
                }
              : {
                  position: "absolute",
                  inset: "0 auto 0 0",
                  width: `${v}%`,
                  background: "var(--primary)",
                  borderRadius: 10,
                  transition: "width 0.2s linear",
                }
          }
        />
      </div>

      {label && (
        <div
          style={{
            fontSize: 12,
            color: "var(--muted-foreground)",
            textAlign: "center",
          }}
        >
          {label}
        </div>
      )}
    </div>
  )
}

export function TRexLoader({
  variant = "overlay",
  value,
  trexTrigger,
  planeCycleMs,
  label,
  className,
}: TRexLoaderProps) {
  const container = usePortalContainer()

  if (variant === "inline") {
    return (
      <TRexWidget
        value={value}
        trexTrigger={trexTrigger}
        planeCycleMs={planeCycleMs}
        label={label}
        className={className}
      />
    )
  }

  // overlay variant — portaled, fixed fullscreen
  return createPortal(
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 50,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        background:
          "color-mix(in srgb, var(--background) 90%, transparent)",
        backdropFilter: "blur(6px)",
      }}
    >
      <TRexWidget
        value={value}
        trexTrigger={trexTrigger}
        planeCycleMs={planeCycleMs}
        label={label}
        className={className}
      />
    </div>,
    container ?? document.body
  )
}

export default TRexLoader
